import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PhotoReference } from '../../models/serializable/file-reference/photo-reference.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class TrailComponent extends RenderableComponent {
  static readonly TYPE_ID: string = '3a29347d2ca2421d826c2d55345880ab';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 3;

  constructor(
    public readonly mapPreview: PhotoReference | null,
    public readonly mapUrl: string | null,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $removable(): boolean {
    return true;
  }

  get $typeId() {
    return TrailComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TrailComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): TrailComponent {
    return new TrailComponent(
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'mapPreview')),
        MapUtils.getOrNull<string>(map, 'mapUrl'),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'mapPreview': context.toDataOrNull(this.mapPreview),
      'mapUrl': this.mapUrl,
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new TrailComponent(
        this.mapPreview?.clone() ?? null,
        this.mapUrl,
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: TrailComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.mapPreview, this.mapPreview) &&
          other.mapUrl === this.mapUrl &&
          other.renderOrder === this.renderOrder &&
          other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class TrailComponentBuilder extends DataModelBuilder<TrailComponent> {
  build(context: SerializationContext, map: SerializedData): TrailComponent {
    return TrailComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TrailComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(TrailComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', TrailComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
